import { useCallback, useMemo, useState } from 'react'

import { type AdditionalCost } from '@api'
import type { ColumnDef, PaginationState } from '@tanstack/react-table'

import IconPlus from '@/assets/icons/icon-plus.svg'
import { Button } from '@/components/ui/button.tsx'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { openDialog } from '@/models/dialogs.ts'
import { useDeleteAdditionalCost } from '@/modules/accounting/hooks/useDeleteAdditionalCost.ts'
import {
  defaultCostValue,
  getAdditionalCostsTableColumns,
} from '@/modules/accounting/ui/AdditionalCosts/constants.tsx'

import { useAccountingAdditionalCosts } from '../../hooks/useAccountingAdditionalCosts'
import { AccountingAdditionalCostsTableSkeleton } from './AccountingAdditionalCostsTableSkeleton.tsx'

interface Props {
  showCents?: boolean
  from: Date
  to: Date
}

export const AccountingAdditionalCostsTable = ({
  showCents = true,
  from,
  to,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const { additionalCosts, isLoading: isAdditionalCostsLoading } =
    useAccountingAdditionalCosts({
      from,
      to,
      page: pagination.pageIndex,
      size: pagination.pageSize,
    })

  const { deleteCost } = useDeleteAdditionalCost()

  const columns: ColumnDef<AdditionalCost>[] = useMemo(
    () => getAdditionalCostsTableColumns({ deleteCost, showCents }),
    [deleteCost, showCents],
  )

  const handleAddClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_ADDITIONAL_COST,
      data: defaultCostValue,
    })
  }, [])

  const handleOpenCostTypesDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ACCOUNTING_ADDITIONAL_COST_TYPES,
    })
  }, [])

  return isAdditionalCostsLoading && !additionalCosts?.content ? (
    <AccountingAdditionalCostsTableSkeleton columns={columns} />
  ) : (
    <DataTable
      hasActionColumn
      title="Additional Costs"
      searchColumn="username"
      columns={columns}
      enablePagination
      paginationState={pagination}
      data={additionalCosts?.content ?? []}
      pageCount={additionalCosts?.totalPages}
      onPaginationChange={setPagination}
      tbodyMinHeight={false}
      extra={
        <div className="flex gap-2">
          <Button
            className="text-sm"
            variant="link"
            onClick={handleOpenCostTypesDialog}
          >
            See all cost types
          </Button>
          <Button onClick={handleAddClick}>
            <IconPlus />
            Add Additional costs
          </Button>
        </div>
      }
    />
  )
}
